'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { LogoVerticalSlogan } from '@/components/logos/LogoVerticalSlogan';
import { LogoSignet } from '@/components/logos/LogoSignet';
import { ApertureToggle } from '@/features/theme/ApertureToggle';
import { MainNavigation } from '@/features/navigation/MainNavigation';
import { useLogoWave, useScrolled } from '@/hooks';
import { cn } from '@/lib/utils/tw-merge';

// Only viewports below 1040px ever open the drawer, so its markup and the
// business-area rows load on demand. `ssr: false` inside this `'use client'`
// module is what actually moves the bytes — a runtime condition would not.
const NavigationDrawer = dynamic(
    () => import('@/features/navigation/NavigationDrawer').then((m) => m.NavigationDrawer),
    { ssr: false },
);

type HeaderProps = {
    className?: string;
    /** Accessible name for the home link — the site title from the settings. */
    siteTitle?: string;
};

/**
 * The site header. Transparent over the hero, solid and frosted once the hero
 * has scrolled past; the vertical lockup shrinks to the signet in the same
 * move, so the sticky state costs the least possible height.
 *
 * The lockup carries the slogan, so it takes a line of its own and the
 * navigation sits underneath it — the initial header is a block, the sticky
 * one the single row it always was. header.css draws both.
 *
 * The mark lights up on arrival and stirs under the pointer in either state —
 * both marks carry the same parts, so the gesture does not change when the
 * lockup gives way to the signet. logo.css draws it.
 */
function Header({ className, siteTitle }: HeaderProps) {
    const isSolid = useScrolled(1);
    // The wave over the mark is CSS; only the ride back out of it needs script.
    const logoRef = useLogoWave<HTMLAnchorElement>();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    // Mounting the lazy drawer is what triggers its request, so it is gated
    // separately: pointer or focus on the burger loads the chunk, the click
    // then only slides the panel in.
    const [isDrawerMounted, setIsDrawerMounted] = useState(false);

    // Above the burger breakpoint the drawer has no trigger left, so an open
    // one could never be closed again.
    useEffect(() => {
        // The switch itself lives in the stylesheet as `--breakpoint-nav`
        // (theme/layout.css), and the header's own media queries read it
        // through Tailwind's `theme()` — which resolves at build time and so
        // leaves nothing here to reach for. Hence the custom property: it is
        // declared `@theme static` for exactly this, so the width is stated
        // once and script and stylesheet cannot drift apart.
        const navSwitch = getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-nav').trim();
        if (!navSwitch) return;

        const query = window.matchMedia(`(min-width: ${navSwitch})`);
        const sync = () => {
            if (query.matches) setIsDrawerOpen(false);
        };

        sync();
        query.addEventListener('change', sync);

        return () => query.removeEventListener('change', sync);
    }, []);

    return (
        <>
            <header className={cn('site-header', isSolid && 'is-solid', className)}>
                <button
                    type="button"
                    className={cn('site-header__burger', isDrawerOpen && 'is-open')}
                    aria-label={isDrawerOpen ? 'Menü schließen' : 'Menü öffnen'}
                    aria-expanded={isDrawerOpen}
                    onClick={() => {
                        setIsDrawerMounted(true);
                        setIsDrawerOpen((open) => !open);
                    }}
                    onPointerEnter={() => setIsDrawerMounted(true)}
                    onFocus={() => setIsDrawerMounted(true)}
                >
                    <span />
                    <span />
                    <span />
                </button>

                <Link ref={logoRef} href="/" className="site-header__logo" aria-label={siteTitle || 'Startseite'}>
                    <span className="site-header__logo-full" aria-hidden="true">
                        <LogoVerticalSlogan />
                    </span>
                    <span className="site-header__logo-signet" aria-hidden="true">
                        <LogoSignet />
                    </span>
                </Link>

                <MainNavigation />

                <ApertureToggle className="site-header__toggle" labelClassName="site-header__toggle-label" />
            </header>

            {isDrawerMounted ? (
                <NavigationDrawer
                    open={isDrawerOpen}
                    onClose={() => setIsDrawerOpen(false)}
                    /* The drawer clears the header it opens under, and that is
                       either of the two heights. Only this component knows
                       which. */
                    compact={isSolid}
                />
            ) : null}
        </>
    );
}

export default Header;
